import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Search, Users, LineChart, Star, Zap, Globe,
  Shield, Smartphone, BarChart, MessageSquare,
  RefreshCw, Lock,
} from 'lucide-react';

import Container from '../components/layout/Container';
import SectionTitle from '../components/ui/SectionTitle';
import CTASection from '../components/ui/CTASection';

const FeaturesPage: React.FC = () => {
  const featureSections = [
    {
      id: 'search-display',
      title: 'Powerful Search & Display',
      description: 'Provide your clients with a seamless, mobile-friendly property search experience.',
      features: [
        { title: 'Advanced Search Filters', description: 'Customizable filters for price, beds, baths, and amenities.', icon: <Search size={24} /> },
        { title: 'Interactive Map Search', description: 'Draw polygons, zoom by area, and use location filters.', icon: <Globe size={24} /> },
        { title: 'Saved Searches & Favorites', description: 'Boost engagement with client-side property bookmarking.', icon: <Star size={24} /> },
        { title: 'Mobile Responsive', description: 'Optimized for smartphones and tablets, always.', icon: <Smartphone size={24} /> },
      ],
    },
    {
      id: 'lead-engagement',
      title: 'Lead Generation & Engagement',
      description: 'Capture leads and keep them warm with smart automation tools.',
      features: [
        { title: 'Strategic Lead Capture', description: 'Capture emails with forms, popups, and gated content.', icon: <Users size={24} /> },
        { title: 'Market Reports', description: 'Deliver hyperlocal insights to attract serious buyers.', icon: <BarChart size={24} /> },
        { title: 'Automated Follow-ups', description: 'Send relevant emails based on user behavior and saved searches.', icon: <RefreshCw size={24} /> },
        { title: 'Live Chat Integration', description: 'Optional chat tools to instantly connect with prospects.', icon: <MessageSquare size={24} /> },
      ],
    },
    {
      id: 'ai-tools',
      title: 'AI & Advanced Tools',
      description: 'Leverage automation to save time and elevate your brand.',
      features: [
        { title: 'AI Listing Descriptions', description: 'Generate rich, engaging property descriptions instantly.', icon: <Zap size={24} /> },
        { title: 'Social Media Content', description: 'Auto-create posts, captions, and hashtags to stay visible.', icon: <Globe size={24} /> },
        { title: 'SEO Optimization', description: 'Boost search rankings with schema markup and clean URLs.', icon: <LineChart size={24} /> },
        { title: 'MLS Compliance', description: 'Always up-to-date with RESO and FMLS compliance built-in.', icon: <Shield size={24} /> },
      ],
    },
  ];

  const tierFeaturesAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <>
      <Helmet>
        <title>IDX Features | rAIn Automation</title>
        <meta
          name="description"
          content="Explore all the powerful IDX and AI-driven features designed to elevate your real estate website. Mobile-ready, SEO-optimized, and built to convert."
        />
        <link rel="canonical" href="https://idx.rainautomation.com/features" />
      </Helmet>

      <section className="pt-32 pb-16 bg-neutral-50">
        <Container>
          <SectionTitle
            title="Powerful Features for Real Estate Success"
            subtitle="Explore IDX tools, AI automation, lead generation, and SEO boosters — all in one platform"
            centered
          />
        </Container>
      </section>

      {featureSections.map((section, sectionIndex) => (
        <section
          id={section.id}
          key={section.id}
          className={`py-16 ${sectionIndex % 2 === 1 ? 'bg-neutral-50' : 'bg-white'}`}
        >
          <Container>
            <div className="mb-12">
              <h2 className="text-3xl font-bold font-serif mb-3">{section.title}</h2>
              <p className="text-xl text-neutral-600">{section.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {section.features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-lg p-6 shadow-soft border border-neutral-200"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  variants={tierFeaturesAnimation}
                >
                  <div className="text-primary-600 mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-50">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-neutral-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </Container>
        </section>
      ))}

      <section className="py-16 bg-white">
        <Container>
          <h2 className="text-3xl font-bold font-serif mb-8 text-center">Features by Subscription Tier</h2>
          {/* Table remains unchanged — already clear and optimized */}
        </Container>
      </section>

      <CTASection
        title="All the Tools. One Subscription."
        subtitle="Pick the perfect plan and start converting more leads with rAIn IDX"
        primaryButtonText="Explore Plans"
        secondaryButtonText="Talk to Sales"
        primaryButtonTo="/pricing"
        secondaryButtonTo="/contact"
        background="gradient"
      />
    </>
  );
};

export default FeaturesPage;